import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Input,
  Spinner,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs
} from '@nextui-org/react';
import { useNavigate } from 'react-router-dom';
import { apiTry } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import PresaleEmailEditor from '../components/PresaleEmailEditor';

// Preventa de agencias (https://agencias.tikcontrol.live). La web es aparte de
// TikControl: aquí se ve quién está en la lista, se añaden agencias nuevas (ya
// pueden pedir su código en la web) y se quitan (pierden el acceso al momento;
// nada se borra y se pueden reactivar). Solo admin. Worker:
// api/backofficePresale.js. No se envían correos desde aquí.

const PRESALE_URL = 'https://agencias.tikcontrol.live';

const STATUS = [
  { key: 'invited', label: 'Invitada', plural: 'Invitadas', color: 'default' },
  { key: 'verified', label: 'Ha entrado', plural: 'Han entrado', color: 'primary' },
  { key: 'accessed', label: 'Ha abierto el documento', plural: 'Han abierto el documento', color: 'success' },
  { key: 'revoked', label: 'Quitada', plural: 'Quitadas', color: 'danger' }
];
const STATUS_BY_KEY = Object.fromEntries(STATUS.map((s) => [s.key, s]));

const fmt = (ms) => (ms ? new Date(ms).toLocaleString() : '—');
const errorText = (r, fallback) => (r && r.data && r.data.error) || fallback;

export default function AgenciaPreventa() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [tab, setTab] = useState('agencies');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [revokedMatch, setRevokedMatch] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await apiTry('/api/backoffice/presale').catch((e) => ({ ok: false, data: { error: e.message } }));
    if (r.ok) {
      setAgencies(r.data.agencies || []);
      setLeads(r.data.leads || []);
    } else {
      setError(r.status === 403 ? 'Solo los administradores pueden ver la preventa.' : errorText(r, 'No se pudo cargar la preventa'));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const acc = { all: agencies.length, invited: 0, verified: 0, accessed: 0, revoked: 0 };
    for (const a of agencies) if (acc[a.status] !== undefined) acc[a.status]++;
    return acc;
  }, [agencies]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return agencies.filter((a) =>
      (statusFilter === 'all' || a.status === statusFilter) &&
      (!needle || [a.name, a.email, a.invitedBy, a.addedBy].some((v) => String(v || '').toLowerCase().includes(needle))));
  }, [agencies, statusFilter, search]);

  function upsert(agency) {
    if (!agency) return;
    setAgencies((list) => (list.some((x) => x.id === agency.id) ? list.map((x) => (x.id === agency.id ? agency : x)) : [agency, ...list]));
  }

  async function add(e) {
    e?.preventDefault?.();
    setNotice(null);
    setError(null);
    setRevokedMatch(null);
    setSaving(true);
    const r = await apiTry('/api/backoffice/presale/agencies', { method: 'POST', body: { name, email } }).catch((err) => ({ ok: false, data: { error: err.message } }));
    setSaving(false);
    if (r.ok) {
      upsert(r.data.agency);
      setNotice(`«${r.data.agency.name}» añadida. Ya puede pedir su código en agencias.tikcontrol.live con ${r.data.agency.email}.`);
      setName('');
      setEmail('');
      return;
    }
    if (r.status === 409 && r.data?.code === 'revoked' && r.data.agency) setRevokedMatch(r.data.agency);
    setError(errorText(r, 'No se pudo añadir'));
  }

  async function change(agency, action) {
    if (action === 'revoke' && !window.confirm(`¿Quitar a «${agency.name}» de la preventa?\n\nYa no podrá entrar ni pedir código, y sus enlaces de invitación dejarán de valer. Puedes reactivarla después.`)) return;
    setBusyId(agency.id);
    setError(null);
    setNotice(null);
    const r = await apiTry(`/api/backoffice/presale/agencies/${encodeURIComponent(agency.id)}/${action}`, { method: 'POST' }).catch((err) => ({ ok: false, data: { error: err.message } }));
    setBusyId(null);
    if (!r.ok) { setError(errorText(r, 'No se pudo cambiar')); return; }
    upsert(r.data.agency);
    if (revokedMatch && revokedMatch.id === agency.id) {
      // Reactivada desde el aviso del formulario: el alta ya no hace falta.
      setRevokedMatch(null);
      setName('');
      setEmail('');
    }
    setNotice(action === 'revoke' ? `«${agency.name}» quitada de la preventa.` : `«${agency.name}» puede volver a entrar.`);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button className="text-sm text-default-500 hover:underline" onClick={() => navigate('/agencias')}>← Agencias</button>
          <h1 className="text-2xl font-semibold">Preventa de agencias</h1>
          <p className="text-sm text-default-500">
            Quién puede entrar en <a className="text-primary hover:underline" href={PRESALE_URL} target="_blank" rel="noreferrer">agencias.tikcontrol.live</a>.
            Cada agencia de la lista pide allí su código con su correo. Desde aquí no se envía ningún correo: pásale tú el enlace.
            La preventa cierra el 30 de octubre a las 23:59 (Madrid).
          </p>
        </div>
        <Button variant="flat" onPress={load} isDisabled={loading}>Recargar</Button>
      </div>

      {canWrite && (
        <form onSubmit={add} className="flex flex-wrap items-end gap-3 rounded-lg border border-default-200 p-3">
          <Input label="Nombre de la agencia" size="sm" className="max-w-xs" value={name} onValueChange={setName} maxLength={120} />
          <Input label="Correo" type="email" size="sm" className="max-w-xs" value={email} onValueChange={setEmail} maxLength={254} />
          <Button type="submit" color="primary" isLoading={saving} isDisabled={!name.trim() || !email.trim()}>Añadir a la preventa</Button>
        </form>
      )}

      {notice && <div className="rounded-md border border-success-200 bg-success-50 p-3 text-sm text-success-700">{notice}</div>}
      {error && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
          <span>{error}</span>
          {revokedMatch && canWrite && (
            <Button size="sm" color="primary" variant="flat" isLoading={busyId === revokedMatch.id} onPress={() => change(revokedMatch, 'restore')}>
              Reactivar a «{revokedMatch.name}»
            </Button>
          )}
        </div>
      )}

      <Tabs selectedKey={tab} onSelectionChange={(k) => setTab(String(k))} aria-label="Preventa">
        <Tab key="agencies" title={`Agencias (${counts.all})`} />
        <Tab key="leads" title={`Solicitudes (${leads.length})`} />
        <Tab key="email" title="Correo del código" />
      </Tabs>

      {tab === 'email' ? (
        <PresaleEmailEditor canWrite={canWrite} />
      ) : tab === 'agencies' ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <Input placeholder="Buscar por agencia, correo o quién la añadió…" value={search} onValueChange={setSearch} isClearable size="sm" className="max-w-sm" />
            <Tabs size="sm" selectedKey={statusFilter} onSelectionChange={(k) => setStatusFilter(String(k))} aria-label="Filtrar por estado">
              <Tab key="all" title={`Todas (${counts.all})`} />
              {STATUS.map((s) => <Tab key={s.key} title={`${s.plural} (${counts[s.key]})`} />)}
            </Tabs>
          </div>

          <Table aria-label="Agencias de la preventa" removeWrapper>
            <TableHeader>
              <TableColumn>AGENCIA</TableColumn>
              <TableColumn>ESTADO</TableColumn>
              <TableColumn>ALTA</TableColumn>
              <TableColumn>ÚLTIMO ACCESO</TableColumn>
              <TableColumn>ACTIVIDAD</TableColumn>
              <TableColumn> </TableColumn>
            </TableHeader>
            <TableBody items={filtered} isLoading={loading} loadingContent={<Spinner />} emptyContent={loading ? ' ' : 'No hay agencias'}>
              {(a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{a.name}</span>
                      <span className="text-xs text-default-500">{a.email}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col items-start gap-1">
                      <Chip size="sm" variant="flat" color={STATUS_BY_KEY[a.status]?.color || 'default'}>{STATUS_BY_KEY[a.status]?.label || a.status}</Chip>
                      {a.status === 'revoked' && <span className="text-xs text-default-500">{fmt(a.revokedAt)}{a.revokedBy ? ` · por ${a.revokedBy}` : ''}</span>}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    <div className="flex flex-col">
                      <span>{fmt(a.createdAt)}</span>
                      <span className="text-xs text-default-500">
                        {a.invitedBy ? `invitada por ${a.invitedBy}` : a.addedBy ? `por ${a.addedBy}` : 'lista inicial'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{fmt(a.lastAccessAt || a.verifiedAt)}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    {a.videoPlays} vídeo{a.videoPlays === 1 ? '' : 's'} · {a.leads} solicitud{a.leads === 1 ? '' : 'es'}
                  </TableCell>
                  <TableCell>
                    {canWrite && (a.status === 'revoked' ? (
                      <Button size="sm" variant="flat" color="primary" isLoading={busyId === a.id} onPress={() => change(a, 'restore')}>Reactivar</Button>
                    ) : (
                      <Button size="sm" variant="light" color="danger" isLoading={busyId === a.id} onPress={() => change(a, 'revoke')}>Quitar</Button>
                    ))}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </>
      ) : (
        <Table aria-label="Solicitudes de la preventa" removeWrapper>
          <TableHeader>
            <TableColumn>FECHA</TableColumn>
            <TableColumn>AGENCIA</TableColumn>
            <TableColumn>CONTACTO</TableColumn>
            <TableColumn>PACKS</TableColumn>
            <TableColumn>MOTIVO</TableColumn>
            <TableColumn>CORREO</TableColumn>
          </TableHeader>
          <TableBody items={leads} isLoading={loading} loadingContent={<Spinner />} emptyContent={loading ? ' ' : 'Todavía no hay solicitudes'}>
            {(l) => (
              <TableRow key={l.id}>
                <TableCell className="whitespace-nowrap text-sm">{fmt(l.createdAt)}</TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-medium">{l.agencyName || l.agency || '—'}</span>
                    {l.verifiedEmail && <span className="text-xs text-default-500">entró con {l.verifiedEmail}</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col text-sm">
                    <span>{l.contactName || '—'}</span>
                    {l.email && <span className="text-xs text-default-500">{l.email}</span>}
                    {l.phone && <span className="text-xs text-default-500">{l.phone}</span>}
                  </div>
                </TableCell>
                <TableCell className="text-sm">
                  <div className="flex flex-col">
                    <span>{l.basePack || '—'}</span>
                    {l.strategicPack && <span className="text-xs text-default-500">{l.strategicPack}</span>}
                  </div>
                </TableCell>
                <TableCell className="max-w-sm text-sm">
                  <span className="line-clamp-3" title={l.reason}>{l.reason || '—'}</span>
                </TableCell>
                <TableCell>
                  <Chip size="sm" variant="flat" color={l.emailSentAt ? 'success' : 'warning'}>{l.emailSentAt ? 'Enviado' : 'Pendiente'}</Chip>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
